import React, { useEffect, useState } from "react";
import axios from "axios";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Pagination from "@mui/material/Pagination";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { TbEye, TbTrash } from "react-icons/tb";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: "flex",
  marginBottom: theme.spacing(2),
  "& .label": {
    fontWeight: "bold",
    minWidth: "100px",
    color: theme.palette.text.secondary,
  },
  "& .value": {
    flex: 1,
    marginLeft: theme.spacing(2),
  },
}));

function Index() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [selectedContact, setSelectedContact] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const token = localStorage.getItem("hiedberg_admin_token");

  const fetchData = async () => {
    try {
      const result = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/contact/get-list?pageNumber=${page}&pageSize=10`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = result?.data?.data || [];
      const totalPage = result?.data?.meta?.totalPages || 0;
      const formattedRows = data.map((item) => ({
        id: item._id,
        name: item.name,
        email: item.email,
        subject: item.subject,
        message: item.message,
        createdAt: item.createdAt,
        updatedAt: item.updatedAt,
        // Store the full item for modal display
        fullData: item,
      }));

      setRows(formattedRows);
      setTotalPage(totalPage);
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedContact(null);
  };

  const handleDeleteContact = (contact) => {
    setContactToDelete(contact);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!contactToDelete) return;
    setIsDeleting(true);
    console.log(token);

    try {
      await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/contact/delete-contact`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            id: contactToDelete.id,
          },
        }
      );
      await fetchData();
      setDeleteModalOpen(false);
      setContactToDelete(null);
    } catch (error) {
      console.error("Error deleting contact:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setContactToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    fetchData();
  }, [page]);

  return (
    <div>
      <div className="rounded-xl border border-gray-300 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="mb-4 text-lg font-semibold text-gray-800">
            Contact List
          </h3>
        </div>

        <div className="flex flex-col gap-y-4">
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Name</StyledTableCell>
                  <StyledTableCell>Email</StyledTableCell>
                  <StyledTableCell>Subject</StyledTableCell>
                  <StyledTableCell>Message</StyledTableCell>
                  <StyledTableCell>Action</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows?.length ? (
                  rows?.map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.subject}</TableCell>
                      <TableCell>
                        {row.message?.length > 50
                          ? `${row.message.substring(0, 50)}...`
                          : row.message}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <div>
                            <TbEye
                              className="h-5 w-5 cursor-pointer"
                              onClick={() => handleViewContact(row)}
                              title="View Details"
                            />
                          </div>
                          <div>
                            <TbTrash
                              onClick={() => {
                                handleDeleteContact(row);
                              }}
                              className="h-5 w-5 cursor-pointer text-red-500 hover:text-red-700"
                            />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No contacts found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {rows.length !== 0 && (
            <Box
              sx={{ display: "flex", justifyContent: "center", marginTop: 2 }}
            >
              <Pagination
                count={totalPage}
                variant="outlined"
                onChange={(event, value) => {
                  setPage(value);
                }}
              />
            </Box>
          )}
        </div>
      </div>

      {/* Contact Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#e0e0e0",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TbEye size={24} />
          Contact Details
        </DialogTitle>
        <DialogContent sx={{ padding: 3 }}>
          {selectedContact && (
            <Box sx={{ marginTop: 1 }}>
              <DetailRow>
                <Typography className="label">Name:</Typography>
                <Typography className="value">
                  {selectedContact.name || "N/A"}
                </Typography>
              </DetailRow>

              <DetailRow>
                <Typography className="label">Email:</Typography>
                <Typography className="value">
                  <a href={`mailto:${selectedContact.email}`}>
                    {selectedContact.email || "N/A"}
                  </a>
                </Typography>
              </DetailRow>

              <DetailRow>
                <Typography className="label">Subject:</Typography>
                <Typography className="value">
                  {selectedContact.subject || "N/A"}
                </Typography>
              </DetailRow>

              <DetailRow>
                <Typography className="label">Message:</Typography>
                <Typography
                  className="value"
                  sx={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    backgroundColor: "grey.50",
                    padding: 2,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                >
                  {selectedContact.message || "N/A"}
                </Typography>
              </DetailRow>

              <DetailRow>
                <Typography className="label">Created:</Typography>
                <Typography className="value">
                  {formatDate(selectedContact.createdAt)}
                </Typography>
              </DetailRow>

              <DetailRow>
                <Typography className="label">Updated:</Typography>
                <Typography className="value">
                  {formatDate(selectedContact.updatedAt)}
                </Typography>
              </DetailRow>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: 2, backgroundColor: "grey.50" }}>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            color="inherit"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={cancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#ffebee",
            color: "#c62828",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <TbTrash size={24} />
          Delete Contact
        </DialogTitle>
        <DialogContent sx={{ padding: 3 }}>
          {contactToDelete && (
            <Box sx={{ marginTop: 1 }}>
              <Typography variant="body1" sx={{ marginBottom: 2 }}>
                Are you sure you want to delete this contact?
              </Typography>
              <Box
                sx={{
                  backgroundColor: "grey.100",
                  padding: 2,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "grey.300",
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  <strong>Name:</strong> {contactToDelete.name}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  <strong>Email:</strong> {contactToDelete.email}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  <strong>Subject:</strong> {contactToDelete.subject}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ marginTop: 2, color: "error.main" }}
              >
                This action cannot be undone.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: 2, backgroundColor: "grey.50" }}>
          <Button
            onClick={cancelDelete}
            variant="outlined"
            color="inherit"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Index;
